import {
  AudioRecorder,
  MicrophonePermissionFlow,
  TemplateList,
  shouldShowTemplateSelection,
  useMicrophoneContext,
} from '@repo/videa-notes';

import { useVideaNotesCoreContext } from '../../../context/VideaNotesCoreContext';
import {
  useRecorderState,
  useRecordingHandlers,
  useTemplateSelection,
} from '../../../context/VideaNotesRecordingContext';
import { SlideOutViewLayout } from '../SlideOutViewLayout';

/**
 * Single flow for template selection + recording. Renders one shared AudioRecorder
 * that stays mounted for the whole flow. When the user is idle with no recording,
 * the template selection UI is shown above the recorder; when recording or after
 * recording, only the recorder (with visualizer) is shown. This avoids unmounting
 * the recorder on transition and prevents state loss or recording failures.
 */
export const RecordingFlowView = () => {
  const { patient, patientName, isVoiceNotesConsentEnabled } =
    useVideaNotesCoreContext();

  const { recorderStatus, mediaBlobUrl } = useRecorderState();
  const showTemplateSelection = shouldShowTemplateSelection({
    mediaBlobUrl,
    recorderStatus,
  });

  const {
    templateOptions,
    selectedTemplate,
    selectedTemplateName,
    selectedTemplateUserInstructions,
    isAudioUploadEnabled,
    showDevTools,
    onSelectedTemplate,
  } = useTemplateSelection();

  const {
    onCancelRecording,
    onCancelRecordingConfirmation,
    onPauseRecording,
    onResumeRecording,
    onStartRecording,
    onStartProviderOnlyRecording,
    onGenerateNote,
    onPatientConsentDenied,
    onPermissionOutcome,
  } = useRecordingHandlers();

  const { hasPermission: hasMicrophonePermission } = useMicrophoneContext();

  return (
    <div className="vai-flex vai-flex-col vai-flex-1 vai-min-h-0 vai-w-full vai-overflow-hidden">
      {/* Permission flow above templates — matches Figma layout */}
      {!hasMicrophonePermission && showTemplateSelection && (
        <div className="vai-flex-shrink-0 vai-w-full">
          <MicrophonePermissionFlow
            onPermissionOutcome={onPermissionOutcome}
            isAudioUploadEnabled={isAudioUploadEnabled}
            showDevTools={showDevTools}
          />
        </div>
      )}

      {showTemplateSelection && (
        <SlideOutViewLayout
          className="vai-items-center"
          header={
            <div className="vai-flex vai-flex-col vai-items-center vai-w-full vai-max-w-2xl vai-gap-4 vai-px-2 vai-pb-2">
              <h2 className="vai-text-[20px] vai-font-semibold vai-text-foreground">
                Select a template to begin
              </h2>
            </div>
          }
          bodyClassName="vai-flex vai-flex-col vai-items-center"
        >
          <div className="vai-flex vai-flex-1 vai-min-h-0 vai-w-full vai-max-w-2xl">
            <TemplateList
              templateOptions={templateOptions}
              selectedTemplate={selectedTemplate}
              onSelectedTemplate={onSelectedTemplate}
              className="vai-flex-1 vai-min-h-0 vai-w-full"
            />
          </div>
        </SlideOutViewLayout>
      )}

      {/* Single AudioRecorder instance – always mounted so recording is never lost on transition */}
      <div
        className={
          showTemplateSelection
            ? 'vai-flex-shrink-0 vai-w-full vai-py-2'
            : 'vai-flex vai-flex-1 vai-min-h-0 vai-w-full'
        }
      >
        <AudioRecorder
          showVisualizer={!showTemplateSelection}
          patientName={patientName}
          patientId={patient?.pmsPatientId ?? 'N/A'}
          templateName={selectedTemplateName}
          templateUserInstructions={selectedTemplateUserInstructions}
          userInstructionsWrapperClassName="vai-w-2/3"
          isTemplateSelected={selectedTemplateName !== null}
          isVoiceNotesConsentEnabled={isVoiceNotesConsentEnabled}
          isAudioUploadEnabled={isAudioUploadEnabled}
          showDevTools={showDevTools}
          externalPermissionFlow={showTemplateSelection}
          onCancelRecording={onCancelRecording}
          onCancelRecordingConfirmation={onCancelRecordingConfirmation}
          onPauseRecording={onPauseRecording}
          onResumeRecording={onResumeRecording}
          onStartRecording={onStartRecording}
          onStartProviderOnlyRecording={onStartProviderOnlyRecording}
          onGenerateNote={onGenerateNote}
          onPatientConsentDenied={onPatientConsentDenied}
          onPermissionOutcome={onPermissionOutcome}
        />
      </div>
    </div>
  );
};
